import { useState, useEffect, useContext, useRef } from 'react';
import SidebarOpenContext from './sidebar_context';
import { toggle as toggleSidebar } from './sidebar_state';
import Tooltip from '../tooltip';
import { translationParsedKeys } from 'react/helpers/i18n';

const t = (key) => {
  try {
    return window.I18n.t(key, translationParsedKeys);
  } catch {
    return key;
  }
};

// Recursively resolve whether an item (at any level) is active.
// Resolution order:
//   1. activeNavKey — server-resolved nav key wins (handles all nested routes
//      via the navigation_registry).
//   2. activeStateKey — namespace-driven flag (kept for Reports etc.).
//   3. currentPath === item.href — exact href fallback for unmapped pages.
const resolveActive = (item, currentPath, activeStates, activeNavKey) => {
  if (activeNavKey && item.key === activeNavKey) return true;
  if (item.children && item.children.length > 0) {
    if (
      item.children.some((child) => resolveActive(child, currentPath, activeStates, activeNavKey))
    ) {
      return true;
    }
  }
  if (item.activeStateKey) return !!(activeStates && activeStates[item.activeStateKey]);
  if (!item.href || item.href === '#') return false;
  return currentPath === item.href;
};

// ── NavList ───────────────────────────────────────────────────────────────────
// Accordion container: only one child open at a time.
// When a child is toggled open, all siblings collapse automatically.
// Extra props (className, data-*, etc.) are forwarded to the <ul>.

export const NavList = ({ items, currentPath, activeStates, activeNavKey, level, ...ulProps }) => {
  const sidebarIsOpen = useContext(SidebarOpenContext);

  const [openKey, setOpenKey] = useState(() => {
    const active = items.find((item) =>
      resolveActive(item, currentPath, activeStates, activeNavKey),
    );
    return active ? active.key : null;
  });

  // Ref-based pending key: set synchronously before toggleSidebar so the
  // useEffect always reads the latest value (avoids React batching race).
  const pendingKeyRef = useRef(null);

  useEffect(() => {
    if (sidebarIsOpen) {
      if (pendingKeyRef.current !== null) {
        setOpenKey(pendingKeyRef.current);
        pendingKeyRef.current = null;
      }
    } else {
      setOpenKey(null);
      pendingKeyRef.current = null;
    }
  }, [sidebarIsOpen]);

  const handleToggle = (key) => {
    if (!sidebarIsOpen) {
      pendingKeyRef.current = key;
      toggleSidebar();
    } else {
      setOpenKey((prev) => (prev === key ? null : key));
    }
  };

  return (
    <ul {...ulProps}>
      {items.map((item) => (
        <NavItem
          key={item.key}
          item={item}
          currentPath={currentPath}
          activeStates={activeStates}
          activeNavKey={activeNavKey}
          level={level || 1}
          isOpen={openKey === item.key}
          onToggle={() => handleToggle(item.key)}
        />
      ))}
    </ul>
  );
};

// ── NavItem ───────────────────────────────────────────────────────────────────
// isOpen / onToggle are provided by NavList (controlled accordion).
// If used standalone (no onToggle), it falls back to internal state.

const NavItem = ({
  item,
  currentPath,
  activeStates,
  activeNavKey,
  level,
  isOpen: isOpenProp,
  onToggle,
}) => {
  const active = resolveActive(item, currentPath, activeStates, activeNavKey);
  const controlled = onToggle !== undefined;
  const [openInternal, setOpenInternal] = useState(() => active);
  const sidebarIsOpen = useContext(SidebarOpenContext);

  // For level-1 plain links: when collapsed, store the target href here,
  // expand the sidebar, then navigate once it opens.
  const pendingHrefRef = useRef(null);
  useEffect(() => {
    if (sidebarIsOpen && pendingHrefRef.current) {
      window.location.href = pendingHrefRef.current;
      pendingHrefRef.current = null;
    }
  }, [sidebarIsOpen]);

  const open = controlled ? isOpenProp : openInternal;
  const toggle = controlled ? onToggle : () => setOpenInternal((p) => !p);

  // ── Level 2+ dropdown (e.g. "Reports" with Level 3 sub-items) ────────────
  if (level >= 2 && item.isDropdown) {
    const hasChildren = item.children && item.children.length > 0;
    return (
      <li
        className={['nav-sub-dropdown', open ? 'is-open' : '', active ? 'nav-active' : '']
          .filter(Boolean)
          .join(' ')}
        data-purpose={item.dataPurpose}
      >
        <div
          className="nav-sub-trigger"
          onClick={() => hasChildren && toggle()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggle();
          }}
        >
          <span>{t(item.label)}</span>
          {hasChildren && (
            <span className={`nav-chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
          )}
        </div>
        {hasChildren && open && (
          <ul className="nav-sub-menu">
            {item.children.map((child) => (
              <NavItem
                key={child.key}
                item={child}
                currentPath={currentPath}
                activeStates={activeStates}
                activeNavKey={activeNavKey}
                level={level + 1}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // ── Level 2 plain link (old icon-dot style) ──────────────────────────────
  if (level === 2) {
    return (
      <li className={['icon-dot', active ? 'nav-active' : ''].filter(Boolean).join(' ')}>
        <a
          href={item.href || '#'}
          data-purpose={item.dataPurpose}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
        >
          {t(item.label)}
        </a>
      </li>
    );
  }

  // ── Level 3+ plain link ───────────────────────────────────────────────────
  if (level >= 3) {
    return (
      <li className={['nav-level-3-item', active ? 'nav-active' : ''].filter(Boolean).join(' ')}>
        <a
          href={item.href || '#'}
          data-purpose={item.dataPurpose}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
        >
          {t(item.label)}
        </a>
      </li>
    );
  }

  // ── Level 1 — unified for ALL roles ──────────────────────────────────────
  // All level-1 items (admin sections, trainer/learner dropdowns, plain links)
  // share the same HTML structure so CSS rules apply once across all roles.
  const hasChildren = item.children && item.children.length > 0;

  const handleTriggerClick = () => {
    if (hasChildren) {
      // Dropdown: toggle submenu (NavList.handleToggle handles sidebar expansion).
      toggle();
    } else if (!sidebarIsOpen) {
      // Plain link, sidebar collapsed: expand sidebar first, navigate via effect.
      pendingHrefRef.current = item.href;
      toggleSidebar();
    } else if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = item.href;
    }
  };

  return (
    <li
      className={[
        'sub-admin-container',
        hasChildren ? 'js-dropdown-toggle' : '',
        item.isSection ? 'nav-section' : '',
        open && hasChildren ? 'is-active' : '',
        active ? 'nav-active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Tooltip title={sidebarIsOpen ? null : t(item.label)} position="right">
        <div
          className={`sub-admin-trigger ${item.icon || ''}`}
          data-purpose={item.dataPurpose}
          onClick={handleTriggerClick}
        >
          <a
            href={hasChildren ? '#' : item.href || '#'}
            onClick={(e) => e.preventDefault()}
            target={!hasChildren && item.external ? '_blank' : undefined}
            rel={!hasChildren && item.external ? 'noopener noreferrer' : undefined}
          >
            {t(item.label)}
          </a>
          {hasChildren && (
            <span className={`nav-chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
          )}
        </div>
      </Tooltip>
      {hasChildren &&
        (item.isSection ? (
          <NavList
            items={item.children}
            currentPath={currentPath}
            activeStates={activeStates}
            activeNavKey={activeNavKey}
            level={2}
            className="nav-section-items sub-admin-menu"
          />
        ) : (
          <ul className="sub-admin-menu">
            {item.children.map((child) => (
              <NavItem
                key={child.key}
                item={child}
                currentPath={currentPath}
                activeStates={activeStates}
                activeNavKey={activeNavKey}
                level={2}
              />
            ))}
          </ul>
        ))}
    </li>
  );
};

export default NavItem;
